/**
 * Firestore implementation of MeasurementRepository.
 *
 * Path convention: measurements/{mcId}
 *
 * Note: kept as a top-level collection rather than a subcollection of
 * orders/{projectId} so a global "Today's measurement visits" query is
 * cheap. ProjectId is stored as a field for order-scoped queries.
 */

import {
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import type { MeasurementConfirmation, EntityId } from '../../../types';
import type { MeasurementRepository } from '../types';

const COLLECTION = 'measurements';

export const firestoreMeasurementRepository: MeasurementRepository = {
  async findById(id: EntityId): Promise<MeasurementConfirmation | null> {
    const snap = await getDoc(doc(db, COLLECTION, id));
    return snap.exists() ? (snap.data() as MeasurementConfirmation) : null;
  },

  // Update-only: rules deny every client create on /measurements, which is
  // the createMeasurementFromQuote Cloud Function's alone (backlog #125).
  async save(entity: MeasurementConfirmation): Promise<void> {
    await setDoc(doc(db, COLLECTION, entity.id), entity);
  },

  async updateFields(id: EntityId, patch: Partial<MeasurementConfirmation>): Promise<void> {
    await updateDoc(doc(db, COLLECTION, id), patch as Record<string, unknown>);
  },

  async delete(id: EntityId): Promise<void> {
    await deleteDoc(doc(db, COLLECTION, id));
  },

  async list(): Promise<MeasurementConfirmation[]> {
    const snap = await getDocs(collection(db, COLLECTION));
    return snap.docs.map((d) => d.data() as MeasurementConfirmation);
  },

  async findByOrder(orderId: EntityId): Promise<MeasurementConfirmation[]> {
    // Single-field query on `orderId`. The dual-field (`projectId` OR `orderId`)
    // migration read was retired 2026-09-04 (backlog #80): prod carried zero
    // `projectId`-only docs and the extra query ran on every order-detail visit.
    // Both writers stamp `orderId` (a `projectId`-only read once hid every MC
    // from MeasurementsList and the OC register — council finding B12).
    const snap = await getDocs(query(collection(db, COLLECTION), where('orderId', '==', orderId)));
    return snap.docs.map((d) => d.data() as MeasurementConfirmation);
  },

  async findBySourceQuote(quoteId: EntityId): Promise<MeasurementConfirmation[]> {
    const q = query(collection(db, COLLECTION), where('sourceQuoteId', '==', quoteId));
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data() as MeasurementConfirmation);
  },

  async findByMeasurer(staffEmail: string): Promise<MeasurementConfirmation[]> {
    const q = query(collection(db, COLLECTION), where('assignedMeasurer', '==', staffEmail));
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data() as MeasurementConfirmation);
  },

  async findByStatus(
    status: MeasurementConfirmation['status'],
  ): Promise<MeasurementConfirmation[]> {
    const q = query(collection(db, COLLECTION), where('status', '==', status));
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data() as MeasurementConfirmation);
  },
};
